import { ChevronDown, X } from 'lucide-react';
import { lazy, Suspense, useState } from 'react';

import { CategoryBadge } from '@/components/CategoryBadge';
import { StatusLabel } from '@/components/StatusLabel';
import { ClaimStatusDropdown } from '@/components/admin/ClaimStatusDropdown';
import { ConfirmChangeModal } from '@/components/admin/ConfirmChangeModal';

import {
  ADMIN_STATUS_TRANSITIONS,
  getEffectiveStatusKey,
  resolveStatusOption,
} from '@/constants/adminStatus';

import { useCategories } from '@/hooks/useCategories';
import { useUpdateClaimCategory } from '@/hooks/useUpdateClaimCategory';
import { useUpdateClaimStatus } from '@/hooks/useUpdateClaimStatus';

import { getReceiptDownloadUrl, type IAdminClaim } from '@/services/admin';

import { loadAiNoteMarkdown, toAiNoteSections } from '@/utils/aiNotes';

const AiNoteMarkdown = lazy(loadAiNoteMarkdown);

interface AdminClaimDetailsModalProps {
  claim: IAdminClaim;
  onClose: () => void;
  onStatusChanged?: () => void;
}

export function AdminClaimDetailsModal({
  claim,
  onClose,
  onStatusChanged,
}: AdminClaimDetailsModalProps) {
  const updateClaimStatusMutation = useUpdateClaimStatus();
  const updateClaimCategoryMutation = useUpdateClaimCategory();
  const { data: categories } = useCategories();
  const [loadingReceipt, setLoadingReceipt] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [statusChangeModal, setStatusChangeModal] = useState<{
    newStatusKey: string;
    newStatus: string;
    newFlag: 'yellow' | 'red' | null;
  } | null>(null);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [pendingCategoryId, setPendingCategoryId] = useState<number | null>(null);

  const claimFlag = claim.flag as 'yellow' | 'red' | null | undefined;
  const currentEffectiveKey = getEffectiveStatusKey(claim.status, claimFlag);
  const validTransitions = ADMIN_STATUS_TRANSITIONS[currentEffectiveKey] || [];
  // Mirrors the backend rule: the category can only change while the claim is in review.
  const canChangeCategory = ['submitted', 'pending', 'pre_approved'].includes(
    String(claim.status).toLowerCase(),
  );

  const handleReceiptClick = async () => {
    if (!claim.receipt || loadingReceipt) return;
    setLoadingReceipt(true);
    const win = window.open('about:blank', '_blank');
    if (win) win.opener = null;
    try {
      const url = await getReceiptDownloadUrl(claim.receipt);
      if (win) win.location.href = url;
    } catch {
      if (win) win.close();
      setSnackbarMessage('Failed to load receipt. Please try again.');
    } finally {
      setLoadingReceipt(false);
    }
  };

  const handleStatusSelect = (optValue: string) => {
    setShowStatusDropdown(false);
    const { status, flag } = resolveStatusOption(optValue);
    setStatusChangeModal({ newStatusKey: optValue, newStatus: status, newFlag: flag });
  };

  const handleConfirmStatusChange = async () => {
    if (!statusChangeModal) return;
    try {
      await updateClaimStatusMutation.updateStatus({
        claimId: claim.id,
        userEmail: claim.userEmail,
        status: statusChangeModal.newStatus,
        flag: statusChangeModal.newFlag,
      });
      setStatusChangeModal(null);
      onStatusChanged?.();
      onClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : null;
      setSnackbarMessage(msg || 'Failed to update claim status. Please try again.');
      setStatusChangeModal(null);
    }
  };

  const categoryName = (id: number) =>
    categories.find((cat) => cat.id === id)?.name ?? claim.categoryName ?? String(id);

  const handleConfirmCategoryChange = async () => {
    if (pendingCategoryId === null) return;
    try {
      await updateClaimCategoryMutation.updateCategory({
        claimId: claim.id,
        userEmail: claim.userEmail,
        category: pendingCategoryId,
      });
      setPendingCategoryId(null);
      onStatusChanged?.();
      onClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : null;
      setSnackbarMessage(msg || 'Failed to update claim category. Please try again.');
      setPendingCategoryId(null);
    }
  };

  const formattedDate = claim.createdAt
    ? new Date(claim.createdAt).toLocaleDateString('en-US', { timeZone: 'UTC' })
    : '—';

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <div className="flex max-h-[90vh] w-full max-w-lg flex-col rounded-lg bg-white shadow-xl">
          {/* Header */}
          <div className="flex shrink-0 items-center justify-between border-b border-gray-100 px-6 py-5">
            <h2 className="text-base font-semibold text-gray-900">Claim Details</h2>
            <button
              type="button"
              onClick={onClose}
              className="-mr-1 rounded p-1 text-gray-400 transition-colors hover:text-gray-600"
            >
              <X size={18} />
            </button>
          </div>

          {/* Body — scrollable only here, not the whole modal */}
          <div className="flex-1 space-y-6 overflow-y-auto p-6">
            {/* X-Teamer */}
            <div>
              <div className="mb-1 text-xs font-medium tracking-wider text-gray-400 uppercase">
                X-Teamer
              </div>
              <div className="text-sm font-semibold text-gray-900">
                {claim.userName || claim.userEmail}
              </div>
            </div>

            {/* Meta — equal 3-column grid */}
            <div className="grid grid-cols-3 gap-0">
              <div>
                <div className="mb-2 text-xs font-medium tracking-wider text-gray-400 uppercase">
                  Category
                </div>
                <CategoryBadge categoryId={claim.category} categoryName={claim.categoryName} />
              </div>
              <div>
                <div className="mb-1 text-xs font-medium tracking-wider text-gray-400 uppercase">
                  Amount
                </div>
                <div className="text-sm font-bold text-gray-900">${claim.amount.toFixed(2)}</div>
              </div>
              <div>
                <div className="mb-1 text-xs font-medium tracking-wider text-gray-400 uppercase">
                  Date
                </div>
                <div className="text-sm text-gray-600">{formattedDate}</div>
              </div>
            </div>

            {/* Status — auto width, not stretched */}
            <div>
              <div className="mb-2 text-xs font-medium tracking-wider text-gray-400 uppercase">
                Status
              </div>
              <StatusLabel status={claim.status} flag={claimFlag} showIcon className="w-fit" />
            </div>

            {/* Details */}
            {claim.details && (
              <div>
                <div className="mb-1 text-xs font-medium tracking-wider text-gray-400 uppercase">
                  Details
                </div>
                <p className="text-sm leading-relaxed whitespace-pre-wrap text-gray-700">
                  {claim.details}
                </p>
              </div>
            )}

            {/* Attachment */}
            {claim.receipt && (
              <div>
                <div className="mb-2 text-xs font-medium tracking-wider text-gray-400 uppercase">
                  Attachment
                </div>
                <button
                  type="button"
                  onClick={handleReceiptClick}
                  disabled={loadingReceipt}
                  className={`flex items-center gap-2 text-sm text-red-600 hover:text-red-800 ${loadingReceipt ? 'cursor-wait opacity-50' : 'cursor-pointer'}`}
                  title={loadingReceipt ? 'Loading…' : 'View receipt'}
                >
                  <span className="text-base">{loadingReceipt ? '⏳' : '📎'}</span>
                  <span className="max-w-[280px] truncate">
                    {claim.receipt.split('/').pop() || 'attachment'}
                  </span>
                </button>
              </div>
            )}

            {/* AI Review Notes */}
            {claim.aiNotes && claim.aiNotes.length > 0 && (
              <div>
                <div className="mb-2 text-xs font-medium tracking-wider text-gray-400 uppercase">
                  AI Review Notes
                </div>
                <ul className="space-y-3">
                  {toAiNoteSections(claim.aiNotes).map((section, i) => (
                    <li key={i} className="text-sm text-gray-700">
                      <div className="font-medium">{section.title}</div>
                      <div className="prose prose-sm max-w-none">
                        <Suspense
                          fallback={<p className="whitespace-pre-wrap">{section.markdown}</p>}
                        >
                          <AiNoteMarkdown markdown={section.markdown} />
                        </Suspense>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Error */}
            {snackbarMessage && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {snackbarMessage}
              </div>
            )}
          </div>

          {/* Footer — outside scroll container so dropdown can overflow freely */}
          <div className="flex shrink-0 flex-wrap gap-6 border-t border-gray-100 px-6 py-5">
            <div>
              <div className="mb-3 text-xs font-medium tracking-wider text-gray-400 uppercase">
                Change Status
              </div>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowStatusDropdown((v) => !v)}
                  disabled={updateClaimStatusMutation.isPending}
                  className={`flex w-[220px] items-center justify-between rounded-lg border border-gray-200 px-4 py-2.5 text-sm text-gray-700 transition-colors hover:bg-gray-50 ${updateClaimStatusMutation.isPending ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                >
                  <span>Select new status…</span>
                  <ChevronDown size={16} className="text-gray-400" />
                </button>
                {showStatusDropdown && (
                  <div className="absolute top-full left-0 z-50 mt-1 w-[220px] rounded-md border border-gray-200 bg-white shadow-lg">
                    <ClaimStatusDropdown
                      currentEffectiveKey={currentEffectiveKey}
                      validTransitions={validTransitions}
                      onSelect={handleStatusSelect}
                    />
                  </div>
                )}
              </div>
            </div>
            <div>
              <label
                htmlFor="admin-claim-category"
                className="mb-3 block text-xs font-medium tracking-wider text-gray-400 uppercase"
              >
                Change Category
              </label>
              <select
                id="admin-claim-category"
                value={claim.category}
                onChange={(e) => {
                  const categoryId = Number(e.target.value);
                  if (categoryId !== claim.category) setPendingCategoryId(categoryId);
                }}
                disabled={
                  !canChangeCategory ||
                  updateClaimCategoryMutation.isPending ||
                  categories.length === 0
                }
                className="w-[220px] rounded-lg border border-gray-200 px-4 py-2.5 text-sm text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {categories
                  .filter((cat) => cat.isActive)
                  .map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {pendingCategoryId !== null && (
        <ConfirmChangeModal
          label="category"
          from={categoryName(claim.category)}
          to={categoryName(pendingCategoryId)}
          onConfirm={() => void handleConfirmCategoryChange()}
          onCancel={() => setPendingCategoryId(null)}
          isLoading={updateClaimCategoryMutation.isPending}
        />
      )}

      {statusChangeModal && (
        <ConfirmChangeModal
          from={currentEffectiveKey}
          to={statusChangeModal.newStatusKey}
          onConfirm={handleConfirmStatusChange}
          onCancel={() => setStatusChangeModal(null)}
          isLoading={updateClaimStatusMutation.isPending}
        />
      )}
    </>
  );
}
